import React, {useState} from "react";
/// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
/// @material-ui/icons
import People from "@material-ui/icons/People";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";

import styles from "./styles";
import { post, useInput } from "../../utility";

const useStyles = makeStyles(styles);
const passwordMeetsPolicy = (password) => password.length >= 8 && /[A-Z]/.test(password) && /\d/.test(password);

export default function ResetPassword() {

    const applySideEffect = (errorState) => {
        return () => {
            if(errorState) {
                setShowWrongUserNamePasswordError(false);
            }
            if(showSuccessBanner) {
                setShowSuccessBanner(false);
            }
        }
    };

    const [ showWrongUserNamePasswordError, setShowWrongUserNamePasswordError ] = useState(false);
    const [ showConfirmPasswordError, setShowConfirmPasswordError ] = useState(false);
    const [ showPasswordPolicyError, setShowPasswordPolicyError ] = useState(false);
    const [ showDBError, setShowDBError ] = useState(false);
    const [ showSuccessBanner, setShowSuccessBanner ] = useState(false);
    const [ userId, setUserId ] = useInput("", applySideEffect(showWrongUserNamePasswordError));
    const [ currentPassword, setCurrentPassword ] = useInput("", applySideEffect(showWrongUserNamePasswordError));
    const [ newPassword, setNewPassword ] = useState("");
    const [ confirmPassword, setConfirmPassword ] = useState("");

    const classes = useStyles();

    const onNewPasswordInputChange = (e) => {
        const nextPassword = e.target.value;
        setNewPassword(nextPassword);
        setShowPasswordPolicyError(nextPassword ? !passwordMeetsPolicy(nextPassword) : false);
        /// only if the confirm password field is not blank, we verify if they are the same
        if(confirmPassword && confirmPassword !== nextPassword) {
            setShowConfirmPasswordError(true);
        } else {
            setShowConfirmPasswordError(false);
        }
        setShowSuccessBanner(false);
    };

    const onConfirmPasswordInputChange = (e) => {
        setConfirmPassword(e.target.value);
        if(newPassword !== e.target.value) {
            setShowConfirmPasswordError(true);
        } else {
            setShowConfirmPasswordError(false);
        }
        setShowSuccessBanner(false);
    };

    const onSubmit = async (event) => {
        if(event) {
            event.preventDefault();
        }

        setShowSuccessBanner(false);
        setShowDBError(false);

        if(newPassword !== confirmPassword) {
            setShowConfirmPasswordError(true);
            return;
        }

        if(!passwordMeetsPolicy(newPassword)) {
            setShowPasswordPolicyError(true);
            return;
        }

        const body = JSON.stringify({
            username: userId,
            currentPassword,
            newPassword
        });
        try {
            const response = await post('/resetPassword', { body });

            const resJson = await response.json();
            if(response.ok && resJson.isSuccessful) {
                setShowSuccessBanner(true);
            } else if(response.ok && !resJson.isSuccessful) {
                setShowWrongUserNamePasswordError(true);
            } else {
                setShowDBError(true);
            }
        } catch (e) {
            setShowDBError(true);
            console.log(e);
        }
    };

    const submitButtonDisabled =
        showWrongUserNamePasswordError ||
        showConfirmPasswordError ||
        showPasswordPolicyError ||
        !userId ||
        !currentPassword ||
        !newPassword ||
        !confirmPassword

    return (
        <div className={classes.page}>
            <section className={classes.hero}>
                <div className={classes.heroOverlay} />
                <div className={classes.brandWrap}>
                    <a href="/login" className={classes.logoLink} aria-label="Go to login page">
                        <img src="/logos/fdl.png" alt="FDL DNT" className={classes.logo} />
                    </a>
                    <span className={classes.brandText}>FDL</span>
                </div>
            </section>

            <div className={classes.resetShell}>
                <form className={classes.form} onSubmit={onSubmit}>
                    <div className={classes.cardHeader}>
                        <h1>Reset Password</h1>
                    </div>
                    <div className={classes.cardBody}>
                        <div className={classes.errorContainer}>
                            <div>
                                { showConfirmPasswordError ? 'New password and confirm password has to be non empty and the same' : ''}
                            </div>
                            <div>
                                { showPasswordPolicyError ? 'New password must be at least 8 characters and include 1 uppercase letter and 1 number' : ''}
                            </div>
                            <div>
                                { showWrongUserNamePasswordError ? 'Incorrect user ID or password, please try again' : '' }
                            </div>
                            <div>
                                { showDBError ? "Oops, something went wrong, please try again later" : "" }
                            </div>
                            <div className={classes.successMessage}>
                                { showSuccessBanner ? "Password reset successfully. You can go back to the login page." : "" }
                            </div>
                        </div>
                        <CustomInput
                            labelText="User ID"
                            formControlProps={{
                                fullWidth: true
                            }}
                            inputProps={{
                                type: "text",
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <People className={classes.inputIconsColor} />
                                    </InputAdornment>
                                ),
                                value: userId,
                                onChange: setUserId
                            }}
                        />
                        <CustomInput
                            labelText="Current password"
                            formControlProps={{
                                fullWidth: true
                            }}
                            inputProps={{
                                type: "password",
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Icon className={classes.inputIconsColor}>
                                            lock_outline
                                        </Icon>
                                    </InputAdornment>
                                ),
                                autoComplete: "off",
                                value: currentPassword,
                                onChange: setCurrentPassword
                            }}
                        />
                        <CustomInput
                            labelText="New password"
                            formControlProps={{
                                fullWidth: true
                            }}
                            inputProps={{
                                type: "password",
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Icon className={classes.inputIconsColor}>
                                            lock_outline
                                        </Icon>
                                    </InputAdornment>
                                ),
                                autoComplete: "off",
                                value: newPassword,
                                onChange: onNewPasswordInputChange
                            }}
                        />
                        <CustomInput
                            labelText="Confirm new password"
                            formControlProps={{
                                fullWidth: true
                            }}
                            inputProps={{
                                type: "password",
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Icon className={classes.inputIconsColor}>
                                            lock_outline
                                        </Icon>
                                    </InputAdornment>
                                ),
                                autoComplete: "off",
                                value: confirmPassword,
                                onChange: onConfirmPasswordInputChange
                            }}
                        />
                        <Button color="primary" className={classes.resetButton} type="submit" disabled={submitButtonDisabled}>
                            Reset
                        </Button>
                    </div>
                </form>
            </div>
            <footer className={classes.footer}>
                © 2026, made with <span>♥</span> by <strong>LongWind</strong> for a better warehouse.
            </footer>
        </div>
    );
}
